import queryString from 'query-string';
import service from '../utility/service';
import { errorOccurred } from '../errors/reducer';

const dataTypes = {
  commodityShortcuts: false,
  baseProducts: false,
  baseProduct2s: false,
  businessGroups: false,
  currencies: false,
  currencyPairs: false,
  deliveryTypes: false,
  finalPriceTypes: false,
  instrumentTypes: false,
  notionalCcys: false,
  notionalCcy2s: false,
  notionalSchedules: false,
  optionExerciseStyles: false,
  optionTypes: false,
  orderTypes: false,
  placesofSettlement: false,
  priceTypes: false,
  referenceRates: false,
  referenceRate2s: false,
  returnorPayoutTriggers: false,
  salesPeople: false,
  settlementCurrencies: false,
  subProducts: false,
  additionalSubProducts: false,
  otherSubProducts: false,
  otherAdditionalSubProducts: false,
  timeZones: false,
  traders: false,
  tradingSystems: false,
  transactionTypes: false,
  underlyingAssetTypes: false,
  underlyingInstrumentIndicies: false,
  unitTypes: false,
  valuationMethodorTriggers: false,
  termofContractUnits: false
};

const dataArrays = {};
Object.keys(dataTypes).forEach((key) => {
  dataArrays[key] = [];
});

const INITIAL_STATE = Object.assign(
  dataArrays,
  {
    fetching: Object.assign({}, dataTypes),
    fetched: Object.assign({}, dataTypes),
    loadedProduct: undefined
  }
);

const types = {
  RECEIVED_DATA: 'orb/staticData/RECEIVED_DATA',
  FETCHING_DATA: 'orb/staticData/FETCHING_DATA',
  FETCHING_CLIENTS: 'orb/staticData/FETCHING_CLIENTS',
  SELECTED_PRODUCT: 'orb/staticData/SELECTED_PRODUCT'
};

const apiPaths = {
  businessGroups: 'BusinessGroups',
  currencies: 'Currencies',
  deliveryTypes: 'DeliveryTypes',
  instrumentTypes: 'InstrumentTypes',
  orderTypes: 'OrderTypes',
  salesPeople: 'SalesPeople',
  traders: 'Traders',
  unitTypes: 'UnitTypes',
  timeZones: 'TimeZones',
  optionTypes: 'OptionTypes',
  optionExerciseStyles: 'OptionExerciseStyles',
  tradingSystems: 'TradingSystems',
  priceTypes: 'PriceTypes',
  currencyPairs: 'CurrencyPairs',
  termofContractUnits: 'TermOfContractUnits'
};

const productStaticPaths = {
  commodityShortcuts: { path: 'CommodityShortcuts' },
  baseProducts: { path: 'BaseProduct' },
  baseProduct2s: { path: 'BaseProduct2' },
  finalPriceTypes: { path: 'FinalPriceType' },
  notionalCcys: { path: 'NotionalCcy' },
  notionalCcy2s: { path: 'NotionalCcy2' },
  notionalSchedules: { path: 'NotionalSchedule' },
  placesofSettlement: { path: 'PlaceofSettlement' },
  referenceRates: { path: 'ReferenceRate' },
  referenceRate2s: { path: 'ReferenceRate2' },
  returnorPayoutTriggers: { path: 'ReturnorPayoutTrigger' },
  settlementCurrencies: { path: 'SettlementCurrency' },
  deliveryTypes: { path: 'DeliveryType' },
  subProducts: {
    path: 'SubProduct',
    dependsOn: ['BaseProduct'],
    getSubUrl: product => `BaseProduct/${product.BaseProduct}/SubProduct`
  },
  additionalSubProducts: {
    path: 'AdditionalSubProduct',
    dependsOn: ['BaseProduct', 'SubProduct'],
    getSubUrl: product => `BaseProduct/${product.BaseProduct}/SubProduct?subProductValue=${product.SubProduct}&additionalSubStaticName=AdditionalSubProduct`
  },
  otherSubProducts: {
    path: 'OtherSubProduct',
    dependsOn: ['BaseProduct2'],
    getSubUrl: product => `BaseProduct2/${product.BaseProduct2}/OtherSubProduct`
  },
  otherAdditionalSubProducts: {
    path: 'OtherAdditionalSubProduct',
    dependsOn: ['BaseProduct2', 'OtherSubProduct'],
    getSubUrl: product => `BaseProduct2/${product.BaseProduct2}/OtherSubProduct?subProductValue=${product.OtherSubProduct}&additionalSubStaticName=OtherAdditionalSubProduct`
  },
  transactionTypes: { path: 'TransactionType' },
  underlyingAssetTypes: { path: 'UnderlyingAssetType' },
  underlyingInstrumentIndicies: { path: 'UnderlyingInstrumentIndex' },
  valuationMethodorTriggers: { path: 'ValuationMethodorTrigger' }
};

const productStaticFields = [
  'InstrumentTypeId',
  'BaseProduct',
  'BaseProduct2',
  'SubProduct',
  'OtherSubProduct'
];

const CLIENT_PAGE_SIZE = 100;

export default function reducer(state = INITIAL_STATE, action = {}) {
  const assign = (obj, propName, value) => {
    const newObj = Object.assign({}, obj);
    newObj[propName] = value;
    return newObj;
  };

  switch (action.type) {
    case types.FETCHING_DATA:
      return Object.assign({}, state, {
        fetching: assign(state.fetching, action.key, true),
        fetched: assign(state.fetched, action.key, false)
      });
    case types.RECEIVED_DATA:
      return assign(
        Object.assign({}, state, {
          fetching: assign(state.fetching, action.key, false),
          fetched: assign(state.fetched, action.key, true)
        }),
        action.key, action.data);
    case types.SELECTED_PRODUCT:
    {
      // Clear static data that depends on instrument id
      const keys = Object.keys(productStaticPaths);
      const data = {};
      const clearFetch = {};
      keys.forEach((key) => {
        if (dataRequiresReset(key, state.loadedProduct, action.product)) {
          data[key] = [];
          clearFetch[key] = false;
        }
      });

      return Object.assign({}, state, data, {
        loadedProduct: action.product,
        fetching: Object.assign({}, state.fetching, clearFetch),
        fetched: Object.assign({}, state.fetched, clearFetch)
      });
    }
    default:
      return state;
  }
}
reducer.types = types;
reducer.initialState = INITIAL_STATE;
reducer.apiPaths = apiPaths;
reducer.productStaticPaths = productStaticPaths;

function dataRequiresReset(key, loadedProduct = {}, newProduct = {}) {
  // Each type has a defined set of fields it depends on, so check if they've changed
  const compareFields = ['InstrumentTypeId'].concat(productStaticPaths[key].dependsOn || []);
  return compareFields.find(field => loadedProduct[field] !== newProduct[field]);
}

function fetchingData(key) {
  return {
    type: types.FETCHING_DATA,
    key
  };
}

function receivedStaticData(key, staticData) {
  return (dispatch) => {
    let data;
    if (staticData.Data) {
      data = staticData.Data;
    } else {
      // TODO: this case should be removed once the API returns
      // the static data in the expected format ('frequent' and
      // 'data' as properties)
      data = staticData;
    }

    if (staticData.Frequent && staticData.Frequent.length > 0) {
      data.frequent = staticData.Frequent;
    }
    dispatch(receivedData(key, data));
  };
}

function receivedData(key, data) {
  return {
    type: types.RECEIVED_DATA,
    key,
    data
  };
}

function selectedProduct(product) {
  return {
    type: types.SELECTED_PRODUCT,
    product
  };
}

function shouldFetchData(state, key) {
  return !state.fetching[key] && !state.fetched[key];
}

function fetchDataIfNeeded(dispatch, state, key, path) {
  if (shouldFetchData(state, key)) {
    dispatch(fetchingData(key));

    return service.get(path).then((response) => {
      dispatch(receivedStaticData(key, response.data));
    }).catch((err) => {
      dispatch(errorOccurred(err, `Getting static data for "${key}"`));
      dispatch(receivedStaticData(key, []));
    });
  }
  return Promise.resolve();
}

export function fetchStaticDataIfNeeded() {
  return (dispatch, getState) => {
    const state = getState().staticData;

    return Promise.all(
      Object.keys(apiPaths).map(key =>
        fetchDataIfNeeded(dispatch, state, key, `/${apiPaths[key]}`)
      )
    );
  };
}

function fetchingClients(input, searchType) {
  return {
    type: types.FETCHING_CLIENTS,
    input,
    searchType
  };
}

export function fetchClients(searchTerms, searchType, pageNum = 1) {
  return (dispatch) => {
    dispatch(fetchingClients(searchTerms, searchType));
    const queryObj = {
      pageSize: CLIENT_PAGE_SIZE,
      pageNum,
      searchType,
      searchTerms,
      firstTerm: searchTerms // TODO: remove after API updated
    };
    const query = queryString.stringify(queryObj);
    return service.post(`/Clients?${query}`);
  };
}

function dispatchChangedProduct(dispatch, state, product) {
  const newProduct = {};
  const oldProduct = state.loadedProduct || {};
  let isChanged = false;
  productStaticFields.forEach((f) => {
    newProduct[f] = product[f];
    isChanged = isChanged || (newProduct[f] !== oldProduct[f]);
  });

  if (isChanged) {
    dispatch(selectedProduct(newProduct));
  }
}

function getProductStaticUrl(key, product) {
  if (productStaticPaths[key].dependsOn) {
    // Don't get if any of the dependsOn fields are empty
    if (productStaticPaths[key].dependsOn.find(field =>
      !product[field] || product[field].length === 0)) {
      return '';
    }
  }

  if (productStaticPaths[key].getSubUrl) {
    return `/BaseProductStatics/${product.InstrumentTypeId}/${productStaticPaths[key].getSubUrl(product)}`;
  }
  return `/ProductStatics/${product.InstrumentTypeId}/${productStaticPaths[key].path}`;
}

export function fetchProductStaticDataIfNeeded(product) {
  return (dispatch, getState) => {
    const instrumentId = product.InstrumentTypeId;

    const allState = getState();
    const state = allState.staticData;
    const fields = allState.isinDetail.fields;

    // Changing product clears data sets
    dispatchChangedProduct(dispatch, state, product);

    if (instrumentId) {
      // Only fetch data for fields we need
      let fetchFields = Object.keys(productStaticPaths)
        .filter(key => !!fields.find(f => f.ProductFieldName === productStaticPaths[key].path));

      // if there is a subProduct field, then add the commodity shortcut field
      if (fetchFields.find(f => f === 'subProducts')) {
        fetchFields = fetchFields.concat('commodityShortcuts');
      }

      return Promise.all(
        fetchFields.map((key) => {
          const url = getProductStaticUrl(key, product);
          if (url) {
            return fetchDataIfNeeded(dispatch, state, key, url);
          }
          return Promise.resolve();
        })
      );
    }
    return Promise.resolve();
  };
}



// WEBPACK FOOTER //
// ./src/staticData/reducer.js